/**
 * Custom horizontal scrollbar attached to any element marked with
 * [data-custom-scrollbar]. The marker should be on the scroll container
 * (overflow-x: auto/scroll). A sibling track + thumb element is injected
 * into the scroller's parent (which must be position: relative).
 *
 * - On scroll/resize, thumb size and position sync with scrollLeft.
 * - On thumb mousedown, drag updates scroller.scrollLeft. window-level
 *   mousemove/mouseup listeners keep dragging stable across browser
 *   pointer-capture transitions (which would otherwise break :hover).
 *
 * Visibility is controlled via CSS by the consuming section
 * (e.g. show on parent hover, hide on mobile, etc.).
 */
(function () {
  'use strict';

  const ATTR = 'data-custom-scrollbar';
  const READY_ATTR = 'data-custom-scrollbar-ready';

  function attach(scroller) {
    if (!(scroller instanceof HTMLElement)) return;
    if (scroller.getAttribute(READY_ATTR) === '1') return;
    scroller.setAttribute(READY_ATTR, '1');

    // scroller を position:relative の wrapper で囲み、scrollbar の bbox を
    // scroller 自身の幅 (page-content と一致) に確実に揃える。
    let host = scroller.parentElement;
    if (!host) return;
    if (!host.classList.contains('custom-scrollbar-host')) {
      const wrapper = document.createElement('div');
      wrapper.className = 'custom-scrollbar-host';
      host.insertBefore(wrapper, scroller);
      wrapper.appendChild(scroller);
      host = wrapper;
    }

    const track = document.createElement('div');
    track.className = 'custom-scrollbar';
    const thumb = document.createElement('div');
    thumb.className = 'custom-scrollbar__thumb';
    track.appendChild(thumb);
    host.appendChild(track);

    function sync() {
      const { scrollLeft, scrollWidth, clientWidth } = scroller;
      if (scrollWidth <= clientWidth + 1) {
        track.style.display = 'none';
        return;
      }
      track.style.display = '';
      const ratio = clientWidth / scrollWidth;
      const widthPct = Math.max(ratio * 100, 8);
      const denom = scrollWidth - clientWidth;
      const leftPct = denom > 0 ? (scrollLeft / denom) * (100 - widthPct) : 0;
      thumb.style.width = widthPct + '%';
      thumb.style.left = leftPct + '%';
    }

    // 初回スクロールで host に印を付ける。「最初は非表示・スクロールしたら表示」の
    // 演出をセクション側CSSで opt-in できるようにする（付与のみで既定挙動は不変）
    scroller.addEventListener(
      'scroll',
      () => host.classList.add('custom-scrollbar-host--scrolled'),
      { once: true, passive: true }
    );

    scroller.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    if (typeof ResizeObserver === 'function') {
      const ro = new ResizeObserver(sync);
      ro.observe(scroller);
    }
    requestAnimationFrame(sync);
    setTimeout(sync, 500);

    let dragging = false;
    let startX = 0;
    let startScrollLeft = 0;
    let pendingLeft = null;
    let rafId = 0;
    let prevScrollBehavior = '';
    let prevSnap = '';

    function flush() {
      rafId = 0;
      if (pendingLeft == null) return;
      scroller.scrollLeft = pendingLeft;
      pendingLeft = null;
    }

    thumb.addEventListener('mousedown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragging = true;
      startX = e.clientX;
      startScrollLeft = scroller.scrollLeft;
      // drag 中の scroll を即時反映するため、smooth-scroll と
      // scroll-snap を一時的に無効化。
      prevScrollBehavior = scroller.style.scrollBehavior;
      prevSnap = scroller.style.scrollSnapType;
      scroller.style.scrollBehavior = 'auto';
      scroller.style.scrollSnapType = 'none';
      document.body.style.userSelect = 'none';
    });

    window.addEventListener('mousemove', (e) => {
      if (!dragging) return;
      e.preventDefault();
      const dx = e.clientX - startX;
      const trackInner = track.clientWidth - thumb.clientWidth;
      const scrollable = scroller.scrollWidth - scroller.clientWidth;
      if (trackInner <= 0 || scrollable <= 0) return;
      pendingLeft = startScrollLeft + (dx / trackInner) * scrollable;
      if (!rafId) rafId = requestAnimationFrame(flush);
    });

    window.addEventListener('mouseup', () => {
      if (!dragging) return;
      dragging = false;
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = 0;
      }
      flush();
      scroller.style.scrollBehavior = prevScrollBehavior;
      scroller.style.scrollSnapType = prevSnap;
      document.body.style.userSelect = '';
    });
  }

  function bindHover(host) {
    if (!(host instanceof HTMLElement)) return;
    if (host.getAttribute('data-scrollbar-hover-ready') === '1') return;
    host.setAttribute('data-scrollbar-hover-ready', '1');
    host.addEventListener('mouseenter', () => host.setAttribute('data-hover', ''));
    host.addEventListener('mouseleave', () => host.removeAttribute('data-hover'));
  }

  function scan(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[' + ATTR + ']').forEach(attach);
    scope.querySelectorAll('[data-scrollbar-hover]').forEach(bindHover);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(document));
  } else {
    scan(document);
  }
  // Late re-scan for sections rendered after first paint.
  setTimeout(() => scan(document), 1500);

  // Expose for sections that mark scrollers after init.
  window.CustomScrollbar = { attach, scan };
})();
